/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Trust-On-First-Use pinning for signed key bundles. Sits on top of
 * `lookup.ts` (which validates a bundle's self-signature) and adds the
 * cross-vault check: does the bundled signing key match what we pinned
 * the first time we saw this identity?
 *
 *   First fetch     → pin the signing-key fingerprint, allow.
 *   Same signer     → allow (encryption-key rotation is fine).
 *   Different signer→ `KeyChangedError`, refuse to send.
 *
 * UI surfacing of `KeyChangedError` is deferred (tracked in the F12
 * follow-up plan).
 */

import { base64nopad } from '@scure/base';
import type {
  PinnedContact,
  VaultContents,
  AuditEvent,
} from '../vault/schema.js';
import {
  fingerprint,
  fingerprintsEqual,
  FINGERPRINT_LENGTH,
} from './fingerprint.js';
import {
  type SignedKeyBundle,
  BUNDLE_ALGORITHM,
  verifyBundleSignature,
} from './lookup.js';

/**
 * Thrown when a signing-key fingerprint differs from the pinned value
 * for an identity. The payload carries both fingerprints (and the
 * pin's `firstSeen`) so callers can render a TOFU-mismatch UI without
 * re-querying the vault.
 */
export class KeyChangedError extends Error {
  readonly identity: string;
  readonly pinnedFingerprint: Uint8Array;
  readonly observedFingerprint: Uint8Array;
  readonly pinnedFirstSeen: string;
  constructor(
    identity: string,
    pinnedFingerprint: Uint8Array,
    observedFingerprint: Uint8Array,
    pinnedFirstSeen: string
  ) {
    super(
      `signing-key fingerprint mismatch for identity '${identity}'; refusing to send`
    );
    this.name = 'KeyChangedError';
    this.identity = identity;
    this.pinnedFingerprint = pinnedFingerprint;
    this.observedFingerprint = observedFingerprint;
    this.pinnedFirstSeen = pinnedFirstSeen;
  }
}

/**
 * Thrown when a caller asks `verifyAgainstPin` for an identity that
 * has never been pinned. Callers should generally use `pinIfNew`
 * which handles both first-seen and subsequent-fetch flows.
 */
export class NotPinnedError extends Error {
  readonly identity: string;
  constructor(identity: string) {
    super(`identity '${identity}' is not pinned in contactKeys`);
    this.name = 'NotPinnedError';
    this.identity = identity;
  }
}

/**
 * Find the pin for `identity` if any. Pure read; never mutates.
 */
const findPin = (
  contents: VaultContents,
  identity: string
): PinnedContact | undefined =>
  contents.contactKeys.find(c => c.identity === identity);

/**
 * Verify a bundle against the existing pin. Returns the verified
 * encryption key bytes (`bundle.keyValue`) on success.
 *
 * @throws {KeyChangedError} if the signing-key fingerprint differs.
 * @throws {NotPinnedError} if no pin exists for `bundle.identity`.
 *         (Callers should use `pinIfNew` for the first-seen flow.)
 */
export const verifyAgainstPin = (
  contents: VaultContents,
  bundle: SignedKeyBundle
): Uint8Array => {
  // Validate the bundle's self-signature first so a tampered bundle
  // can't slip past pinning by riding on a forged signing key.
  verifyBundleSignature(bundle);

  const pin = findPin(contents, bundle.identity);
  if (!pin) {
    throw new NotPinnedError(bundle.identity);
  }
  const observed = fingerprint(bundle.signingKey);
  if (!fingerprintsEqual(pin.fingerprint, observed)) {
    throw new KeyChangedError(
      bundle.identity,
      pin.fingerprint,
      observed,
      pin.firstSeen
    );
  }
  return bundle.keyValue;
};

/**
 * First-seen TOFU pin if no pin exists; otherwise verify against the
 * existing pin. Returns:
 *   - `contents` — possibly-new VaultContents (with the pin appended
 *     and an audit-log entry) if `isNewPin === true`; otherwise the
 *     input untouched.
 *   - `keyValue` — the verified encryption key bytes.
 *   - `isNewPin` — whether this call created a new pin.
 *
 * @throws {KeyChangedError} if a pin exists but the signing-key fingerprint differs.
 * @throws {BundleSignatureError} if the bundle's self-signature fails.
 */
export const pinIfNew = (
  contents: VaultContents,
  bundle: SignedKeyBundle,
  now: Date = new Date()
): {
  contents: VaultContents;
  keyValue: Uint8Array;
  isNewPin: boolean;
} => {
  // Always verify the bundle's self-signature, whether pinning or
  // re-checking. A first-seen pin must still be a valid signed bundle
  // — otherwise an attacker who controls first-contact could pin
  // arbitrary bytes as the "signing key".
  verifyBundleSignature(bundle);

  const existing = findPin(contents, bundle.identity);
  const observed = fingerprint(bundle.signingKey);

  if (existing) {
    if (!fingerprintsEqual(existing.fingerprint, observed)) {
      throw new KeyChangedError(
        bundle.identity,
        existing.fingerprint,
        observed,
        existing.firstSeen
      );
    }
    // Same signer → encryption-key rotation is fine. No pin update,
    // no audit entry — we use the verified `keyValue` and move on.
    return { contents, keyValue: bundle.keyValue, isNewPin: false };
  }

  // First-seen flow: append a new PinnedContact and an audit entry.
  const newPin: PinnedContact = {
    identity: bundle.identity,
    fingerprint: observed,
    firstSeen: now.toISOString(),
    algorithm: BUNDLE_ALGORITHM,
  };
  const audit: AuditEvent = {
    at: now.toISOString(),
    kind: 'pinned',
    details: {
      identity: bundle.identity,
      fingerprint: base64nopad.encode(observed),
      algorithm: BUNDLE_ALGORITHM,
    },
  };
  const updated: VaultContents = {
    ...contents,
    updatedAt: now.toISOString(),
    contactKeys: [...contents.contactKeys, newPin],
    auditLog: [...contents.auditLog, audit],
  };
  // Sanity: the fingerprint we just stored is the canonical 32-byte
  // SHA-256 — anything else means a misuse from the underlying
  // primitive and would silently break later verifications.
  if (newPin.fingerprint.length !== FINGERPRINT_LENGTH) {
    throw new Error(
      `internal: fingerprint length ${newPin.fingerprint.length} ≠ ${FINGERPRINT_LENGTH}`
    );
  }
  return { contents: updated, keyValue: bundle.keyValue, isNewPin: true };
};
